import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle, Clock, BarChart2, PieChart } from "lucide-react";
import { insightsApi, IRGInsights } from "../../services/incidentService";
import Toast, { type ToastMessage } from "../common/Toast";
import { IncidentInsightsSkeleton } from "../common/InsightsSkeleton";
import NoDataFound from "../common/NoDataFound";
import { usePermission } from "../../contexts/PermissionContext";

const ACT_INSIGHTS = "IRG_REPORTS";

const EMPTY_INSIGHTS: IRGInsights = {
    needs_triage: 0, open_investigations: 0, avg_age_days: 0,
    closed_this_month: 0, type_distribution: [], department_heatmap: [],
};

const TYPE_COLORS = ['#8b5cf6', '#6366f1', '#3b82f6', '#06b6d4', '#10b981'];
const RISK_COLOR: Record<string, string> = { High: '#ef4444', Medium: '#f59e0b', Low: '#10b981' };

const KpiCard = ({ label, value, sub, icon: Icon, iconBg, iconColor }: any) => (
    <div className="relative bg-white rounded-2xl flex items-center gap-4 px-5 py-5 cursor-default overflow-hidden"
        style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.2s ease, transform 0.2s ease' }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.11), 0 2px 6px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}>
        {iconColor && <div className="absolute bottom-0 right-0 w-24 h-24 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${iconColor}0d 0%, transparent 70%)`, transform: 'translate(30%, 30%)' }} />}
        <div className="shrink-0 p-3 rounded-xl" style={{ background: iconBg, boxShadow: `0 2px 10px ${iconColor}28` }}>
            <Icon size={20} style={{ color: iconColor }} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate mb-1">{label}</p>
            <span className="text-[2rem] font-extrabold text-slate-800 leading-none tabular-nums">{value}</span>
            {sub && <p className="text-[10px] text-slate-400 mt-1 truncate">{sub}</p>}
        </div>
    </div>
);

const DistributionWidget = ({ title, data, icon: Icon, total, accentColor = '#6366f1', className = '' }: any) => {
    const maxCount = Math.max(...data.map((d: any) => d.count), 1);
    return (
        <div className={`rounded-2xl overflow-hidden flex flex-col ${className}`}
            style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg" style={{ background: `${accentColor}12` }}>
                        <Icon size={12} style={{ color: accentColor }} />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
                </div>
                {total > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tabular-nums" style={{ background: `${accentColor}12`, color: accentColor }}>{total}</span>}
            </div>
            {data.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8 italic">No data yet.</p>
            ) : (
                <ul className="divide-y divide-slate-50 flex-1">
                    {data.map((item: any, idx: number) => {
                        const color = item.color || TYPE_COLORS[idx % TYPE_COLORS.length];
                        const barPct = item.count > 0 ? Math.round((item.count / maxCount) * 100) : 0;
                        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                        return (
                            <li key={idx} className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                                style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">{item.label}</span>
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                                    <div className="h-1.5 rounded-full transition-all duration-500"
                                        style={{ width: barPct > 0 ? `${barPct}%` : '0%', background: `linear-gradient(90deg, ${color}bb, ${color})`, boxShadow: barPct > 10 ? `0 1px 4px ${color}55` : 'none' }} />
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <span className="text-[12px] font-extrabold tabular-nums" style={{ color: item.count > 0 ? color : '#94a3b8' }}>{item.count}</span>
                                    <span className="text-[10px] text-slate-400">({pct}%)</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const DepartmentWidget = ({ data, className = '' }: any) => {
    const maxCount = Math.max(...data.map((d: any) => d.count), 1);
    const accentColor = '#ef4444';
    return (
        <div className={`rounded-2xl overflow-hidden flex flex-col ${className}`}
            style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg" style={{ background: `${accentColor}12` }}><BarChart2 size={12} style={{ color: accentColor }} /></div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Department Heatmap</span>
                </div>
            </div>
            {data.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8 italic">No open incidents tagged to a department yet.</p>
            ) : (
                <ul className="divide-y divide-slate-50 flex-1" data-testid="LST_incident_department_heatmap">
                    {data.map((d: any, idx: number) => {
                        const color = RISK_COLOR[d.risk] || '#94a3b8';
                        const barPct = d.count > 0 ? Math.round((d.count / maxCount) * 100) : 0;
                        return (
                            <li key={idx} className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                                style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">{d.label}</span>
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                                    <div className="h-1.5 rounded-full transition-all duration-500"
                                        style={{ width: barPct > 0 ? `${barPct}%` : '0%', background: `linear-gradient(90deg, ${color}bb, ${color})`, boxShadow: barPct > 10 ? `0 1px 4px ${color}55` : 'none' }} />
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <span className="text-[12px] font-extrabold tabular-nums" style={{ color }}>{d.count}</span>
                                    <span className="text-[10px] text-slate-400 uppercase">({d.risk})</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const IncidentInsights = () => {
    const [data, setData] = useState<IRGInsights>(EMPTY_INSIGHTS);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_INSIGHTS, 'view');

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setLoading(true);
        insightsApi.get()
            .then(d => { if (!cancelled) setData(d); })
            .catch(e => { if (!cancelled) setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Failed to load insights' }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [canView]);

    const totalForPie = useMemo(() => data.type_distribution.reduce((s, r) => s + r.count, 0), [data.type_distribution]);
    const typeData = data.type_distribution.map((r, i) => ({ label: r.label, count: r.count, color: TYPE_COLORS[i % TYPE_COLORS.length] }));

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to incident insights." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full min-h-[500px]">
            <div className="netbody-content">
                <div className="scroll-container-nofilters flex-1 overflow-hidden">
                    {loading && <IncidentInsightsSkeleton />}
                    {!loading && (
                        <div className="py-4 space-y-4">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                <KpiCard label="Needs Triage" value={data.needs_triage} sub="Awaiting review" icon={AlertTriangle} iconBg="#fef2f2" iconColor="#ef4444" />
                                <KpiCard label="Open Investigations" value={data.open_investigations} sub={`Avg age: ${data.avg_age_days.toFixed(1)} days`} icon={Clock} iconBg="#eff6ff" iconColor="#3b82f6" />
                                <KpiCard label="Closed This Month" value={data.closed_this_month} sub="Triaged & archived" icon={CheckCircle} iconBg="#ecfdf5" iconColor="#10b981" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <DistributionWidget title="Incident Types Distribution" data={typeData} icon={PieChart} total={totalForPie} accentColor="#8b5cf6" />
                                <DepartmentWidget data={data.department_heatmap} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default IncidentInsights;
